import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Target, Users, Zap } from 'lucide-react';
import { ABOUT_TEXT, IMAGERY } from '@/lib/content';
import SEO from '@/components/SEO';

const VALUES = [
    { icon: Target, title: 'Direct response first', body: 'Every spot, every placement is built to drive a measurable action — calls, walk-ins, web leads.' },
    { icon: Zap, title: 'Speed of broadcast', body: 'We turn briefs into on-air creative in days, not months — without ever cutting corners on craft.' },
    { icon: Users, title: 'Personalised service', body: 'You work with the principals, not a layered account team. Direct, candid, accountable.' },
    { icon: Award, title: 'Three decades on air', body: 'Three decades buying media, producing creative, and pressure-testing direct response strategies.' },
];

export default function About() {
    return (
        <div className="pt-28 bg-white">
            <SEO title="About" description="With over 30 years of experience, CFL Ad Group is a leader in direct response advertising for automotive dealerships." path="/about" />
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
                <div className="text-xs uppercase tracking-[0.35em] font-bold text-brand-orange mb-5">
                    About CFL Ad Group
                </div>
                <h1 className="font-display font-black text-brand-navy text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-5xl">
                    A direct response agency built<br />
                    <span className="italic font-medium text-brand-orange">in the broadcast trenches.</span>
                </h1>
            </section>

            <section className="bg-brand-navy text-white">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-18 lg:py-20 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden">
                            <img src={IMAGERY.studio} alt="CFL Ad Group production studio" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="lg:col-span-7 lg:pl-6">
                        <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-orange mb-4">
                            Our Story
                        </div>
                        <h2 className="font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight mb-6 leading-tight">
                            {ABOUT_TEXT.headline}
                        </h2>
                        <p className="text-white/80 text-base md:text-lg leading-relaxed">
                            {ABOUT_TEXT.body}
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10 pt-10 border-t border-white/15">
                            {ABOUT_TEXT.stats.map((s) => (
                                <div key={s.label}>
                                    <div className="font-display font-extrabold text-4xl md:text-5xl text-brand-orange leading-none">
                                        {s.value}
                                    </div>
                                    <div className="text-[11px] uppercase tracking-[0.25em] text-white/55 mt-2">
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-brand-cream py-14 md:py-18 lg:py-20">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="grid lg:grid-cols-12 gap-12 mb-10 lg:mb-12 items-end">
                        <div className="lg:col-span-7">
                            <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-orange mb-4">
                                How we work
                            </div>
                            <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-[44px] text-brand-navy tracking-tight leading-tight">
                                Four principles. <span className="italic font-medium text-brand-orange">Zero fluff.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-5">
                            <img src={IMAGERY.dealership} alt="CFL Ad Group on location" className="w-full h-64 object-cover" />
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {VALUES.map((v) => (
                            <div key={v.title} className="bg-white border border-brand-navy/10 p-8 hover:border-brand-orange transition-colors">
                                <v.icon className="w-7 h-7 text-brand-orange mb-6" strokeWidth={1.5} />
                                <h3 className="font-display font-extrabold text-xl text-brand-navy mb-3 leading-tight">{v.title}</h3>
                                <p className="text-brand-navy/65 text-sm leading-relaxed">{v.body}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 lg:mt-12">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-navy text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                        >
                            Schedule a call <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

